import React, { useEffect } from "react";
import styled from "styled-components";
import { breakpoints } from "@trailhead/asurion-ui";
import { FlexColumnContainer } from "@trailhead/core-ui";

import { updateMxHeader } from "@trailhead/mx-header";
import { trackEvent, updateStickyProps } from "@trailhead/analytics";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { useMxContext } from "@trailhead/mx-context";
import { ChatPrompts, defaultChatPrompts, triggerExpertMessagingOverlay } from "../components";
import { useQueryParams } from "@trailhead/smart-setup-core";
import { useChatMessage } from "@trailhead/contentful";
import { AEClientType, useChatClient } from "@trailhead/chat";
import { TwilioClientType } from "@trailhead/chat";
import "./chatonly.css";

const ExpertSetupContainer = styled(FlexColumnContainer)`
  color: #000000;
  align-items: flex-start;
  margin: 0 auto;
  max-width: 600px;
  width: 100%;
  height: 70vh;
  border: 3px solid rgb(228, 228, 228);
  border-radius: 8px;
  ${breakpoints.md} {
    justify-content: flex-start;
    align-content: flex-start;
    text-align: left;
  }
`;

const moreDetailsBotMessages = [
  "Thanks for reaching out! Before we connect you to the most suitable expert, can you please tell us your name?",
  "Hi there, please let us know how can we help you today.",
];

const isIncludedInArray = (
  searchString: string | null | undefined,
  arrayToSearch: string[],
) => {
  if (searchString === null || searchString === undefined) return false;
  return arrayToSearch.some(arrVal => searchString.includes(arrVal));
};

const attachChatPrompts = (
  chatPrompts: ChatPrompts[],
  shouldAutoSend: boolean,
  chatClient: TwilioClientType | AEClientType,
) => {
  const messageSection = document.getElementsByClassName(
    "timeline-messages-section",
  )[0];

  //rewrite first message
  if (
    messageSection.childNodes[1].childNodes[0].childNodes[0].childNodes[0]
      .textContent
  ) {
    messageSection.childNodes[1].childNodes[0].childNodes[0].childNodes[0].textContent =
      "We're here to help you get set up on your new device or anything else you need.";
  }

  //add additional message "from expert"
  const extraMessage = document.createElement("div");
  extraMessage.innerHTML = `
    <div class="TimelineItem">
      <div class="TimelineItemMessageWrapper">
        <div class="TimelineItemMessage">
          <div class="TimelineItemMessageText">How can we help you? Choose an option below, or ask us anything.</div>
        </div>
      </div>
    </div>
  `;

  messageSection.append(extraMessage);

  //add chat suggestions
  chatPrompts.forEach(prompt => {
    const div = document.createElement("div");
    div.innerHTML = `
      <div class="ChatButtonContainer">
        <button class="ChatButton" type="button">${prompt.buttonText}</button>
      </div>
    `;

    div.onclick = () =>
      triggerExpertMessagingOverlay(
        chatClient,
        prompt.chatText,
        shouldAutoSend,
        true,
      );
    messageSection.append(div);
  });
};

const attachToContainer = async (
  chatClient: TwilioClientType | AEClientType,
) => {
  await chatClient.showMessaging();
  await chatClient.attachToContainer("chat-sdk-container");
};

export const ChatOnly: React.FC = () => {
  const { client } = useMxContext();
  const t = useSanitizedTranslation();
  const queryParams = useQueryParams();
  const chatClient = useChatClient();

  const cid = queryParams.get("cid");

  updateStickyProps({ pathfinderFlow: true, pathfinderVersion: "F", cid });
  const shouldAutoSend = true;
  const chatPrompts = defaultChatPrompts;

  const hubUrl = "https://my.asurion.com";

  updateMxHeader({
    view: "service-view",
    title: t("Mobile Device Set Up"),
    homePath: hubUrl,
    client,
  });

  useEffect(() => {
    updateStickyProps({ route: "/setup" });
    trackEvent("expert_setup_page_load");
    trackEvent("Page_Loaded", {
      pageName: "Pathfinder_Page",
      pathfinderVariant: "ChatOnly_F",
      cid,
    });
  }, []);

  const utmContent = queryParams.get("utm_content");
  const utmMessage = useChatMessage(utmContent);
  const hiddenText = (chat: TwilioClientType | AEClientType) =>
    utmContent
      ? utmMessage
        ? chat.sendHiddenMessage(utmMessage.chatMessage)
        : console.log("Error: no associated message found for ${utmContent}")
      : chat.sendHiddenMessage("I need help");

  useEffect(() => {
    attachToContainer(chatClient);
    // Select the node that will be observed for mutations
    const targetNode = document.getElementById("chat-sdk-container");

    // Options for the observer (which mutations to observe)
    const config = { attributes: true, childList: true, subtree: true };

    // Callback function to execute when mutations are observed
    const hiddenMessageCallback = function (
      mutationsList: MutationRecord[],
      observer: MutationObserver,
    ) {
      for (const mutation of mutationsList) {
        if (
          mutation?.addedNodes.item(0)?.textContent?.includes("Let's get going")
        ) {
          hiddenText(chatClient);
          observer.disconnect();
          break;
        }
      }
    };

    const chatPromptCallback = function (
      mutationsList: MutationRecord[],
      observer: MutationObserver,
    ) {
      for (const mutation of mutationsList) {
        if (
          isIncludedInArray(
            mutation?.addedNodes.item(0)?.textContent,
            moreDetailsBotMessages,
          )
        ) {
          if (
            chatPrompts != null &&
            shouldAutoSend != null &&
            utmContent == null
          ) {
            attachChatPrompts(chatPrompts, shouldAutoSend, chatClient);
          }
          observer.disconnect();
          break;
        }
      }
    };

    if (chatPrompts != null && shouldAutoSend != null) {
      // Create an observer instance linked to the callback function
      const hiddenMessageObserver = new MutationObserver(hiddenMessageCallback);
      const chatPromptObserver = new MutationObserver(chatPromptCallback);
      // Start observing the target node for configured mutations
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      hiddenMessageObserver.observe(targetNode, config);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      chatPromptObserver.observe(targetNode, config);
    }
  }, [
    chatClient,
    chatPrompts,
    hiddenText,
    shouldAutoSend,
    utmContent,
    utmMessage,
  ]);

  return <ExpertSetupContainer id="chat-sdk-container" />;
};

export default ChatOnly;
